import { LocalDateTime } from "@js-joda/core";
import assertNever from "assert-never";
import { API } from "../../../API";
import {
  CaregiverId,
  DocumentWithExpiryAnswer,
  GeneralSignatureAnswer,
  HRQuestion,
  HRQuestionGroup,
  HRQuestionGroupId,
  HRQuestionId,
  HRQuestionTemplateId,
  HRSection,
  HRSubSection,
  HRSubSectionId,
  OrientationLanguages,
} from "../../../messages";
import { langMap } from "../../../translate";
import { assertUndefinedable } from "../../../utils/assertUndefinedable";
import { mergeImages } from "../../../utils/imageUtils";
import { fmap } from "../../../utils/fmap";
import { isDefined } from "../../Generic/IsDefined";
import { OnboardingForm } from "../../OnboardingSection/hooks/useOnboardingForm";

export type DocumentQuestion<T extends HRQuestion> = T & {
  type: "Document" | "DocumentWithExpiry";
};
export type RejectedQuestion = HRQuestion & {
  answerRejectionStatus: HRQuestion.HRQuestionRejectionStatusRejected;
};
export type StageStatus = ReturnType<typeof getStageStatus>;
export type SectionCompletionStatus = ReturnType<typeof getSectionCompletionStatus>;
export type HRQuestionWithGroup = HRQuestion & { questionGroup: HRQuestionGroup };

export type SectionInfoItemData =
  | {
      type: "pendingDocument";
      question: HRQuestionWithGroup;
      section: HRSection;
    }
  | {
      type: "pendingEmailVerification";
      question: HRQuestionWithGroup;
      section: HRSection;
    }
  | {
      type: "pendingSection";
      section: HRSection;
    }
  | {
      type: "rejectedDocument";
      question: HRQuestionWithGroup;
      section: HRSection;
    }
  | {
      type: "rejectedQuestion";
      question: HRQuestion;
      section: HRSection;
    }
  | {
      type: "completedQuestions";
      numCompleted: number;
      numTotal: number;
    }
  | {
      type: "buttonStart";
      section: HRSection;
    }
  | {
      type: "buttonContinue";
      section: HRSection;
    }
  | {
      type: "buttonReview";
      section: HRSection;
    };

const questionTypesWithoutAnswers: HRQuestion.QuestionType[] = [
  "TitleText",
  "PlainText",
  "Placeholder",
  "Video",
];

export function mapQuestionAnswer(question: HRQuestion) {
  switch (question.type) {
    case "Text":
    case "Dollar":
    case "Signature":
    case "StartAndEndTime":
    case "Star":
    case "Document":
    case "DocumentWithExpiry":
    case "DownloadableFile":
    case "Address":
    case "Email":
    case "Numeric":
    case "PhoneNumber":
    case "SSN":
    case "FullDate":
    case "BirthDate":
    case "MonthYearDate":
    case "YearDate":
    case "GeneralSignature":
      return question.answer;

    case "Radio":
    case "DropDown":
    case "AmericanTest":
    case "Interview":
      return question.chosenAnswerId?.toString() ?? null;

    case "FreeText":
    case "Checkbox":
      return fmap(question.chosenAnswerIds, (chosenAnswerIds) => chosenAnswerIds.map(String));

    case "TitleText":
    case "PlainText":
    case "Placeholder":
    case "Video":
      return null;

    default:
      assertNever(question);
  }
}

export function getQuestionWithAnswer(question: HRQuestion, answer: unknown): HRQuestion {
  switch (question.type) {
    case "Text":
    case "Dollar":
    case "Signature":
    case "Document":
    case "DownloadableFile":
    case "Address":
    case "Email":
    case "Numeric":
    case "PhoneNumber":
    case "FullDate":
    case "BirthDate":
    case "MonthYearDate":
    case "YearDate":
      return {
        ...question,
        answer: typeof answer === "string" ? answer.trim() : null,
      };

    case "GeneralSignature":
      return {
        ...question,
        answer: isDefined(answer) ? (answer as GeneralSignatureAnswer) : null,
      };

    case "StartAndEndTime":
      return {
        ...question,
        answer: isDefined(answer) ? (answer as { startTime: number; endTime: number }) : null,
      };

    case "Star":
      return {
        ...question,
        answer: typeof answer === "number" ? answer : null,
      };

    case "DocumentWithExpiry":
      return {
        ...question,
        answer: isDefined(answer) ? (answer as DocumentWithExpiryAnswer) : null,
      };

    case "SSN":
      return {
        ...question,
        answer: typeof answer === "string" ? answer.replace(/\D/g, "") : null,
      };

    case "Radio":
    case "DropDown":
    case "AmericanTest":
      return {
        ...question,
        chosenAnswerId: isDefined(answer) ? Number(answer) : null,
      };

    case "FreeText":
    case "Checkbox":
      return {
        ...question,
        chosenAnswerIds: (Array.isArray(answer) ? answer : [answer]).map(Number),
      };

    case "Interview":
      return {
        ...question,
        chosenAnswerId: isDefined(answer) ? Number(answer) : null,
        answer: typeof answer === "string" ? answer : null,
      };

    case "TitleText":
    case "PlainText":
    case "Placeholder":
    case "Video":
      return question;

    default:
      assertNever(question);
  }
}

export function mapSectionToQuestionGroup(section: HRSection): HRQuestionGroup[] {
  let questionGroups: HRQuestionGroup[] = [];

  for (const subSection of section.subSections) {
    questionGroups = questionGroups.concat(subSection.questionGroups);
  }

  return questionGroups;
}

export function mapQuestionGroupsToQuestion(
  questionGroup: HRQuestionGroup[]
): HRQuestionWithGroup[] {
  return questionGroup.flatMap((group) =>
    group.questions.map((question) => ({ ...question, questionGroup: group }))
  );
}

export function isSectionInProgress(section: HRSection) {
  return hasStartedSection(section) && !isSectionCompleted(section);
}

export function isSectionVisible(section: HRSection): boolean {
  return (
    section.subSections.find(
      (subSection) => subSection.data.settings?.hideOnApplication !== true
    ) !== undefined
  );
}

export function isQuestionAnswered(question: HRQuestion) {
  return (
    question.answeredAt !== undefined ||
    ("answer" in question && question.answer !== null && question.answer !== "") ||
    ("chosenAnswerId" in question && question.chosenAnswerId !== null) ||
    ("chosenAnswerIds" in question &&
      question.chosenAnswerIds !== null &&
      question.chosenAnswerIds.length > 0)
  );
}

export function sectionHasPendingDocuments(
  section: HRSection,
  isCriminalBackgroundCompliant: boolean,
  isInterviewCompliant: boolean | null
) {
  if (
    section.data.title.toLowerCase() === "criminal background" &&
    isSectionCompleted(section) &&
    !isCriminalBackgroundCompliant
  ) {
    return true;
  }

  if (
    isInterviewSection(section) &&
    isSectionCompleted(section) &&
    (isInterviewCompliant === false || isInterviewCompliant === null)
  ) {
    return true;
  }

  return (
    mapSectionToQuestionGroup(section).find((questionGroup) =>
      questionGroup.questions.find(
        (q) => isQuestionPendingDocument(q) || isEmailVerificationQuestionPending(q)
      )
    ) !== undefined
  );
}

export function isQuestionPendingDocument<T extends HRQuestion>(
  question: T
): question is DocumentQuestion<T> {
  return (
    "complianceRejectionData" in question &&
    isQuestionAnswered(question) &&
    question.complianceRejectionData?.type === "Pending"
  );
}

export function isEmailVerificationQuestionPending<T extends HRQuestion>(question: T) {
  return (
    question.emailVerificationRejectionStatus !== null &&
    question.emailVerificationRejectionStatus.type === "Pending" &&
    question.answerRejectionStatus.status !== "Rejected"
  );
}

export function isDocumentQuestion<T extends HRQuestion>(
  question: T
): question is DocumentQuestion<T> {
  return question.type === "Document" || question.type === "DocumentWithExpiry";
}

export function notApprovedDocumentQuestion<T extends HRQuestion, K extends DocumentQuestion<T>>(
  question: K
): boolean {
  return question.complianceRejectionData?.type === "Rejected";
}

export function sectionHasRejectedDocuments(section: HRSection) {
  return (
    mapSectionToQuestionGroup(section).find((questionGroup) =>
      questionGroup.questions.find(
        (question) =>
          "complianceRejectionData" in question &&
          (question.complianceRejectionData?.type === "Rejected" ||
            question.complianceRejectionData?.type === "Dismissed") &&
          !question.isHidden
      )
    ) !== undefined
  );
}

export function isSectionLocked(
  section: HRSection,
  isCriminalBackgroundCompliant: boolean,
  isInterviewCompliant: boolean | null
) {
  return (
    section.data.locked &&
    getSectionCompletionStatus(section, isCriminalBackgroundCompliant, isInterviewCompliant) ===
      "Not Started"
  );
}

export function isI9SectionWithRejectedDocuments(section: HRSection): boolean {
  return sectionHasRejectedDocuments(section) && section.data.title.toLowerCase() === "i-9";
}

export function sectionHasRejectedQuestionAnswers(section: HRSection) {
  return mapSectionToQuestionGroup(section).some(stageHasRejectedQuestionAnswers);
}

export function stageHasRejectedQuestionAnswers(stage: HRQuestionGroup) {
  return stage.questions.some(stageQuestionIsRejected);
}

export function stageHasRejectedDocuemntQuestionAnswers(stage: HRQuestionGroup) {
  return stage.questions.some(stageDocumentQuestionIsRejected);
}

export function getStageRejectedQuestionsDataMap(
  stage: HRQuestionGroup
): Map<HRQuestionId, string> {
  const mapArray: [HRQuestionId, string][] = [];
  for (const question of stage.questions) {
    if (question.answerRejectionStatus.status === "Rejected") {
      mapArray.push([question.id, question.answerRejectionStatus.reason]);
    }
    if (question.type === "Document" && question.complianceRejectionData?.type === "Rejected") {
      mapArray.push([question.id, question.complianceRejectionData.reason ?? "Rejected"]);
    }
  }

  return new Map(mapArray);
}

export function stageQuestionIsRejected(question: HRQuestion): question is RejectedQuestion {
  return question.answerRejectionStatus.status === "Rejected";
}

export function stageDocumentQuestionIsRejected(question: HRQuestion): question is HRQuestion & {
  complianceRejectionData: {
    type: "Rejected";
    reason: string | null;
  };
} {
  return (
    "complianceRejectionData" in question &&
    (question.complianceRejectionData?.type === "Rejected" ||
      question.complianceRejectionData?.type === "Dismissed") &&
    !question.isHidden
  );
}

export function stageHasRejectedDocumentComplianceQuestions(group: HRQuestionGroup) {
  return group.questions.some(
    (question) =>
      "complianceRejectionData" in question &&
      question.complianceRejectionData?.type === "Rejected" &&
      !question.isHidden
  );
}

export function hasStartedSection(section: HRSection) {
  return (
    mapSectionToQuestionGroup(section).find((questionGroup) =>
      questionGroup.questions.find((q) => isQuestionAnswered(q) && q.hideOnMobile !== true)
    ) !== undefined
  );
}

export function isSectionCompleted(section: HRSection) {
  return isSectionCompletedByQuestionGroups(mapSectionToQuestionGroup(section));
}

export function isQuestionRequired(question: HRQuestion) {
  return question.isMandatory && isRelevantQuestion(question);
}

export function isSectionCompletedByQuestionGroups(questionGroups: HRQuestionGroup[]) {
  return (
    questionGroups.find((questionGroup) =>
      questionGroup.questions
        .filter(isQuestionRequired)
        .find((question) => !isQuestionCompleted(question))
    ) === undefined
  );
}

export function isQuestionApproved(question: HRQuestion) {
  return (
    "complianceRejectionData" in question &&
    isQuestionAnswered(question) &&
    question.complianceRejectionData?.type === "Approved"
  );
}

export function isQuestionCompleted(question: HRQuestion) {
  return (
    isQuestionAnswered(question) &&
    !stageQuestionIsRejected(question) &&
    !stageDocumentQuestionIsRejected(question)
  );
}

export function isSectionReadyToSignByQuestionGroups(
  currentStage: HRQuestionGroup,
  questionGroups: HRQuestionGroup[],
  questionGroupIdToSubsectionId: Map<HRQuestionGroupId, HRSubSectionId>
) {
  const subSectionId = questionGroupIdToSubsectionId.get(currentStage.data.id);

  return (
    questionGroups
      .filter(
        (questionGroup) => questionGroupIdToSubsectionId.get(questionGroup.data.id) === subSectionId
      )
      .find((questionGroup) =>
        questionGroup.questions.find(
          (question) =>
            !isQuestionAnswered(question) &&
            question.isMandatory &&
            !question.isHidden &&
            question.type !== "Signature" &&
            isAnswerableQuestion(question)
        )
      ) === undefined
  );
}

export function isRelevantQuestion(question: HRQuestion) {
  return !question.isHidden && question.hideOnMobile !== true && isAnswerableQuestion(question);
}

export function isAnswerableQuestion(question: HRQuestion) {
  return !questionTypesWithoutAnswers.includes(question.type);
}

export function isQuestionGroupContainSignatureQuestion(questionGroup: HRQuestionGroup) {
  return isDefined(questionGroup.questions.find((question) => question.type === "Signature"));
}

export function isCriminalBackgroundSection(section: HRSection) {
  return section.data.title.toLowerCase() === "criminal background";
}

export function isReferenceSection(section: HRSection) {
  return section.data.title.toLowerCase() === "references";
}

export function getSectionTitle(section: HRSection, language: string) {
  if (section.data.translations === null) {
    return section.data.title;
  }

  const preferredLang = section.data.translations[language as OrientationLanguages];

  return preferredLang?.title ?? section.data.title;
}

export function getQuestionsLeft(section: HRSection) {
  const questions = mapQuestionGroupsToQuestion(mapSectionToQuestionGroup(section));
  const relevantQuestions = questions.filter((x) => !x.isHidden && isAnswerableQuestion(x));
  const answeredQuestions = relevantQuestions.filter(
    (x) =>
      isQuestionAnswered(x) &&
      x.answerRejectionStatus.status !== "Rejected" &&
      (!("complianceRejectionData" in x) ||
        !["Rejected", "Dismissed"].includes(x.complianceRejectionData?.type ?? ""))
  );
  return relevantQuestions.length - answeredQuestions.length;
}

export function getSubSectionTitle(subSection: HRSubSection, language: string) {
  if (subSection.data.translations === null) {
    return subSection.data.title;
  }

  const preferredLang = subSection.data.translations[language as OrientationLanguages];

  return preferredLang?.title ?? subSection.data.title;
}

export function getQuestionGroupTitle(questionGroup: HRQuestionGroup, language: string) {
  if (questionGroup.data.translations === null) {
    return questionGroup.data.title;
  }

  const preferredLang = questionGroup.data.translations[language as OrientationLanguages];

  return preferredLang?.title ?? questionGroup.data.title;
}

export function getDocumentQuestionName(params: {
  question: HRQuestionWithGroup;
  language: string;
}) {
  const { question, language } = params;

  const languageVal = langMap[language];

  if (question.type === "Document" || question.type === "DocumentWithExpiry") {
    return (
      question.documentTypeNameTranslations?.[languageVal] ?? getQuestionText(question, language)
    );
  }

  return getQuestionGroupTitle(question.questionGroup, language);
}

export function getQuestionText(question: HRQuestion, language: string) {
  if (question.questionTextTranslations === null) {
    return question.questionText;
  }

  const preferredLang = question.questionTextTranslations[language as OrientationLanguages];

  return preferredLang ?? question.questionText;
}

export function getPossibleAnswers(question: HRQuestion, language: string) {
  if (!("possibleAnswers" in question)) {
    return [];
  }

  if (question.possibleAnswersTranslations === null) {
    return question.possibleAnswers;
  }

  const preferredLang = question.possibleAnswersTranslations[language as OrientationLanguages];

  return preferredLang ?? question.possibleAnswers;
}

function isEmptyAnswer(answer: unknown) {
  return answer === null || answer === undefined || answer === "";
}

function areAnswersEqual(answer1: unknown, answer2: unknown) {
  if (answer1 === answer2) {
    return true;
  }

  if (isEmptyAnswer(answer1) && isEmptyAnswer(answer2)) {
    return true;
  }

  if (Array.isArray(answer1) && Array.isArray(answer2)) {
    return answer1.length === answer2.length && answer1.every((x) => answer2.includes(x));
  }

  if (typeof answer1 === "object" && typeof answer2 === "object") {
    return JSON.stringify(answer1) === JSON.stringify(answer2);
  }

  return false;
}

export function shouldSkipQuestionSubmittion(
  data: OnboardingForm,
  initialValues: OnboardingForm,
  question: HRQuestion,
  isStageReadOnly: boolean,
  emailVerificationQuestionTemplaeId: HRQuestionTemplateId | null
) {
  const fieldName = getQuestionFieldName(question);

  if (isStageReadOnly) {
    return true;
  }

  if (assertUndefinedable(data[fieldName]) === undefined) {
    return true;
  }

  const questionWithAnswer = getQuestionWithAnswer(question, data[fieldName]);

  if (areAnswersEqual(mapQuestionAnswer(questionWithAnswer), initialValues[fieldName])) {
    if (
      question.textId === emailVerificationQuestionTemplaeId &&
      question.answerRejectionStatus.status === "Rejected"
    ) {
      return false;
    }
    return true;
  }

  if (isEmptyAnswer(data[fieldName]) && isEmptyAnswer(initialValues[fieldName])) {
    return true;
  }

  return false;
}

export function getFileType(url: string): "image" | "pdf" {
  if (url.startsWith("data:application/pdf")) {
    return "pdf";
  }

  if (url.startsWith("data:image")) {
    return "image";
  }

  const extension = url.split(".").pop()?.split("?").shift()?.toLowerCase();

  return extension === "pdf" ? "pdf" : "image";
}

export async function sendHrApplicationAnswer(params: {
  api: API;
  question: HRQuestion;
  caregiverId: CaregiverId;
}) {
  const question = await getQuestionWithCombinedAssets(params);

  return params.api.setHRApplicationAnswer(question);
}

export async function getQuestionWithCombinedAssets(params: {
  api: API;
  question: HRQuestion;
  caregiverId: CaregiverId;
}): Promise<HRQuestion> {
  if (params.question.type !== "Document") {
    return params.question;
  }

  const answer = params.question.answer;

  if (answer === null) {
    return params.question;
  }

  const urls = answer.split("|");

  if (urls.length === 1) {
    return params.question;
  }

  if (urls.some((url) => getFileType(url) !== "image")) {
    const blobs = await Promise.all(
      urls.map((url) => fetch(url).then((response) => response.blob()))
    );

    const result = await params.api.uploadDocuments(params.caregiverId, blobs);

    const s3Keys = result.documents;

    return {
      ...params.question,
      answer: s3Keys.join(","),
    };
  }

  const base64 = await mergeImages(urls);

  return {
    ...params.question,
    answer: base64,
  };
}

export function getSectionCompletionStatus(
  section: HRSection,
  isCriminalBackgroundCompliant: boolean,
  isInterviewCompliant: boolean | null
) {
  if (sectionHasRejectedDocuments(section) || sectionHasRejectedQuestionAnswers(section)) {
    return "Rejected" as const;
  }

  if (isSectionInProgress(section)) {
    return "Incomplete" as const;
  }

  if (sectionHasPendingDocuments(section, isCriminalBackgroundCompliant, isInterviewCompliant)) {
    return "Pending Review" as const;
  }

  if (isSectionCompleted(section)) {
    return "Completed" as const;
  }

  return "Not Started" as const;
}

export function getStageStatus(
  stage: HRQuestionGroup,
  isSectionCompleted: boolean,
  isSubsectionCompliant: boolean | null
) {
  if (stageHasRejectedQuestionAnswers(stage) || stageHasRejectedDocuemntQuestionAnswers(stage)) {
    return "rejected";
  }

  const questions = stage.questions.filter(isAnswerableQuestion);

  if (questions.every(isQuestionApproved)) {
    return "approved";
  }

  if (questions.every(isQuestionAnswered)) {
    if (questions.find(isEmailVerificationQuestionPending) !== undefined) {
      return "pendingEmail";
    }

    if (questions.some(isQuestionPendingDocument)) {
      return "pending";
    }

    return isSectionCompleted || isSubsectionCompliant === true ? "completed" : "pending";
  }

  return "not_answered";
}

export function getQuestionFieldName(question: HRQuestion) {
  return question.id.toString();
}

export function getSectionInfoItems(params: {
  section: HRSection;
  isCriminalBackgroundCompliant: boolean;
  isInterviewCompliant: boolean | null;
  questions: HRQuestionWithGroup[];
  answeredQuestions: HRQuestionWithGroup[];
  completedQuestions: HRQuestionWithGroup[];
  questionGroupIdComplianceMap: Map<HRQuestionGroupId, boolean | null>;
}) {
  const {
    section,
    isCriminalBackgroundCompliant,
    questions,
    answeredQuestions,
    completedQuestions,
    isInterviewCompliant,
    questionGroupIdComplianceMap,
  } = params;

  if (
    isSectionCompleted(section) &&
    !sectionHasPendingDocuments(section, isCriminalBackgroundCompliant, isInterviewCompliant)
  ) {
    return [];
  }

  const items: SectionInfoItemData[] = [];

  const numQuestionsLeft = questions.length - answeredQuestions.length;

  answeredQuestions.filter(isQuestionPendingDocument).forEach((question) => {
    items.push({
      type: "pendingDocument",
      question,
      section,
    });
  });

  const pendingEmailQuestion = answeredQuestions.find(isEmailVerificationQuestionPending);
  if (pendingEmailQuestion !== undefined) {
    items.push({
      type: "pendingEmailVerification",
      question: pendingEmailQuestion,
      section,
    });
  }

  if (
    isCriminalBackgroundSection(section) &&
    !isCriminalBackgroundCompliant &&
    isSectionCompleted(section)
  ) {
    items.push({
      type: "pendingSection",
      section,
    });
  }

  if (
    isInterviewSection(section) &&
    isSectionCompleted(section) &&
    (isInterviewCompliant === null || !isInterviewCompliant)
  ) {
    items.push({
      type: "pendingSection",
      section,
    });
  }

  const questionTextIdsThatAreActuallyDocuments = [
    "MedicalsFluVacSignature_medflyt",
    "MedicalsHepBVacSignature_medflyt",
  ].map(HRQuestionTemplateId.wrap);

  questions
    .filter((question) => questionTextIdsThatAreActuallyDocuments.includes(question.textId))
    .forEach((question) => {
      if (stageQuestionIsRejected(question)) {
        items.push({
          type: "rejectedDocument",
          question,
          section,
        });
      } else if (
        isQuestionPending(question) &&
        questionGroupIdComplianceMap.get(question.questionGroup.data.id) !== true
      ) {
        items.push({
          type: "pendingDocument",
          question,
          section,
        });
      }
    });

  answeredQuestions
    .filter(isDocumentQuestion)
    .filter(notApprovedDocumentQuestion)
    .forEach((question) => {
      items.push({
        type: "rejectedDocument",
        question,
        section,
      });
    });

  answeredQuestions
    .filter((question) => !isDocumentQuestion(question))
    .filter(stageQuestionIsRejected)
    .forEach((question) => {
      items.push({
        type: "rejectedQuestion",
        question,
        section,
      });
    });

  if (completedQuestions.length > 0) {
    items.push({
      type: "completedQuestions",
      numCompleted: completedQuestions.length,
      numTotal: questions.length,
    });
  }

  if (items.length > 0) {
    if (!hasStartedSection(section)) {
      items.push({
        type: "buttonStart",
        section,
      });
    } else if (numQuestionsLeft > 0) {
      items.push({
        type: "buttonContinue",
        section,
      });
    } else {
      items.push({
        type: "buttonReview",
        section,
      });
    }
  }

  return items;
}

export function isQuestionPending(question: HRQuestion) {
  return (
    isQuestionAnswered(question) &&
    !stageQuestionIsRejected(question) &&
    !isQuestionApproved(question)
  );
}

export function isInterviewSection(section: HRSection) {
  return section.data.title.toLowerCase().includes("interview");
}

export function isInterviewQuestion<T extends HRQuestion>(
  question: T
): question is T & HRQuestion.InterviewQuestion {
  return question.type === "Interview";
}

export function getInterviewDate(section: HRSection) {
  if (!isInterviewSection(section)) {
    return null;
  }

  const questions = mapQuestionGroupsToQuestion(mapSectionToQuestionGroup(section));

  const dateString = questions.find(isInterviewQuestion)?.answer ?? null;

  if (dateString !== null) {
    try {
      const meetingStart = new Date(dateString);
      return LocalDateTime.of(
        meetingStart.getFullYear(),
        meetingStart.getMonth() + 1,
        meetingStart.getDate(),
        meetingStart.getHours(),
        meetingStart.getMinutes()
      );
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  return null;
}
